import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { delftBlue } from "@/theme/colors";
import CompanyStatsCards from "./components/CompanyStatsCards";
import CompanyTable from "./components/CompanyTable";
import ActivitiesPagination from "./components/ActivitiesPagination";
import CollapsibleSection from "./components/CollapsibleSection";
import {
  useGetCompaniesQuery,
  useGetCompanyStatsQuery,
  useUpdateCompanyStatusMutation,
  useDeleteCompanyMutation
} from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const CompaniesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");

  const {
    data: companiesResponse,
    isLoading: isLoadingCompanies,
    error: companiesError,
    refetch: refetchCompanies
  } = useGetCompaniesQuery({ page, limit, search });

  const {
    data: statsResponse,
    isLoading: isLoadingStats,
    error: statsError,
    refetch: refetchStats
  } = useGetCompanyStatsQuery();

  // Refetch data when navigating back from form page
  useEffect(() => {
    if (location.state?.refresh) {
      refetchCompanies();
      refetchStats();
      // Clear the state to prevent refetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, refetchCompanies, refetchStats, navigate, location.pathname]);

  const [updateCompanyStatus, { isLoading: isUpdating }] = useUpdateCompanyStatusMutation();
  const [deleteCompany, { isLoading: isDeleting }] = useDeleteCompanyMutation();

  // Check if user can add companies (only Super Admin)
  const canAddCompany = user?.role === Roles.SUPER_ADMIN;

  // Extract data from API response
  const companiesData = companiesResponse?.data || {
    companies: [],
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: limit,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };

  const statsData = statsResponse?.data || {
    total: 0,
    active: 0,
    inactive: 0,
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page
  };

  const handleView = (company) => {
    navigate(`/companies/${company._id || company.id}/edit`);
  };

  const handleEdit = (company) => {
    navigate(`/companies/${company._id || company.id}/edit`);
  };

  const handleAddCompany = () => {
    navigate("/companies/add");
  };

  const handleActivate = async (company) => {
    try {
      await updateCompanyStatus({
        id: company._id || company.id,
        status: !company.isActive
      }).unwrap();
      refetchCompanies();
    } catch (error) {
      console.error("Failed to update company status:", error);
      alert(error?.data?.message || "Failed to update company status");
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Are you sure you want to delete "${company.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteCompany(company._id || company.id).unwrap();
      refetchCompanies();
      refetchStats();
    } catch (error) {
      console.error("Failed to delete company:", error);
      alert(error?.data?.message || "Failed to delete company");
    }
  };

  const handleInviteAdmin = (company) => {
    const existingAdmin =
      company?.adminId && typeof company.adminId === "object"
        ? company.adminId
        : null;

    navigate("/companies/invite-admin", {
      state: {
        companyId: company._id || company.id,
        companyName: company.name,
        existingAdmin,
      },
    });
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ alignSelf: "stretch" }}>
        {/* Header Section */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 331.6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 22, sm: 24, md: 28 },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.29,
                width: "100%",
              }}
            >
              Companies
            </Typography>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Manage and monitor all companies
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards */}
        <CollapsibleSection
          title="Company Statistics"
          defaultExpanded={true}
          showCard={false}
        >
          {isLoadingStats ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : statsError ? (
            <Alert severity="error">
              {statsError?.data?.message || "Failed to load statistics"}
            </Alert>
          ) : (
            <CompanyStatsCards
              stats={statsData}
              onAddCompany={canAddCompany ? handleAddCompany : undefined}
            />
          )}
        </CollapsibleSection>

        {/* Companies Table */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, sm: 4, md: 6 },
          }}
        >
          {isLoadingCompanies ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : companiesError ? (
            <Alert severity="error">
              {companiesError?.data?.message || "Failed to load companies"}
            </Alert>
          ) : (
            <>
              <CompanyTable
                companies={companiesData.companies}
                onView={handleView}
                onEdit={handleEdit}
                onActivate={handleActivate}
                onDelete={handleDelete}
                onInviteAdmin={handleInviteAdmin}
                isLoading={isUpdating || isDeleting}
              />

              {/* Pagination */}
              <ActivitiesPagination
                page={companiesData.pagination?.currentPage || page}
                limit={limit}
                total={companiesData.pagination?.totalItems || 0}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default CompaniesPage;
